import { Fragment } from "react";
import { Dialog, Popover, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  FilterIcon,
  LocationMarkerIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  XIcon,
} from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import Loader from "../../../components/loader/Loader";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const STATUS_STYLES = {
  U: { label: "Submitted", className: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  A: { label: "Received", className: "border-blue-200 bg-blue-50 text-blue-700" },
  S: { label: "Assigned", className: "border-amber-200 bg-amber-50 text-amber-700" },
  W: { label: "In Progress", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  R: { label: "Review", className: "border-purple-200 bg-purple-50 text-purple-700" },
  C: { label: "Completed", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  I: { label: "Invoiced", className: "border-sky-200 bg-sky-50 text-sky-700" },
  T: { label: "Canceled", className: "border-slate-200 bg-slate-100 text-slate-700" },
};

const TAG_STYLES = {
  red: "border-red-200 bg-red-50 text-red-700",
  orange: "border-orange-200 bg-orange-50 text-orange-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  fuchsia: "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700",
  pink: "border-pink-200 bg-pink-50 text-pink-700",
  slate: "border-slate-200 bg-white text-slate-600",
  lime: "border-lime-200 bg-lime-50 text-lime-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cyan: "border-cyan-200 bg-cyan-50 text-cyan-700",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
};

function MessageSquareTextIcon({ className }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
      <path d="M7 11h10" />
      <path d="M7 15h6" />
      <path d="M7 7h8" />
    </svg>
  );
}

function AirplaneIcon({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 4 2 2 4 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.3c.4-.2.6-.6.5-1.1Z" />
    </svg>
  );
}

function getServiceNames(job) {
  const regular = (job.job_service_assignments || []).map(
    (assignment) => assignment.service_name || assignment.service?.name || assignment.name,
  );
  const retainer = (job.job_retainer_service_assignments || []).map(
    (assignment) => assignment.service_name || assignment.retainer_service?.name || assignment.name,
  );
  const categories = (job.categories || []).map(
    (category) => category.customer_category?.name || category.name,
  );

  const names = [...regular, ...retainer].filter(Boolean);
  return names.length ? names : categories.filter(Boolean);
}

function formatPurchaseOrder(value) {
  if (!value) return null;
  return String(value).startsWith("#") ? value : `#${value}`;
}

function getTagLabel(tag) {
  return tag.tag_short_name?.trim() || tag.name?.trim() || "";
}

function StatusBadge({ status }) {
  const config = STATUS_STYLES[status] || {
    label: status || "Unknown",
    className: "border-slate-200 bg-slate-100 text-slate-700",
  };
  const label = status === "A" ? "Confirmed" : config.label;

  return (
    <span className={classNames("inline-flex rounded-md border px-2.5 py-1 text-xs font-bold", config.className)}>
      {label}
    </span>
  );
}

function UnreadCommentsBadge({ count, compact = false }) {
  if (!count) return null;

  if (compact) {
    return (
      <span className="grid h-7 w-7 place-items-center rounded-full bg-[#E3222A] text-xs font-bold text-white" aria-label={`${count} unread comments`}>
        {count}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#E3222A] px-2 py-1 text-xs font-bold text-white" aria-label={`${count} unread comments`}>
      <MessageSquareTextIcon className="h-3 w-3" />
      <span aria-hidden="true">{count}</span>
    </span>
  );
}

function ServicesSummary({ job }) {
  const services = getServiceNames(job);
  const extraServiceCount = services.length - 1;
  const visibleTags = (job.tags || []).filter((tag) => getTagLabel(tag));

  return (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">Service</p>
      <div className="relative mt-1 flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
          {services[0] || "No service specified"}
        </span>
        {extraServiceCount > 0 && (
          <Popover className="pointer-events-auto static flex-none md:relative">
            {({ open }) => (
              <>
                <Popover.Button
                  className="flex-none rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-bold text-sky-700 shadow-sm hover:border-sky-300 hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  aria-label={`Show all ${services.length} services`}
                >
                  +{extraServiceCount} more
                </Popover.Button>
                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="translate-y-1 scale-95 opacity-0"
                  enterTo="translate-y-0 scale-100 opacity-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="translate-y-0 scale-100 opacity-100"
                  leaveTo="translate-y-1 scale-95 opacity-0"
                >
                  <Popover.Panel
                    static
                    data-testid="customer-services-popover"
                    className="absolute inset-x-0 top-full z-50 mt-2 w-auto origin-top rounded-lg bg-white p-3 text-left shadow-lg ring-1 ring-slate-900/10 md:left-0 md:right-auto md:w-72 md:origin-top-left"
                  >
                    <h3 className="text-sm font-extrabold text-slate-900">{services.length} services</h3>
                    <ul className="mt-2 space-y-2">
                      {services.map((service, index) => (
                        <li key={`${service}-${index}`} className="flex items-start gap-2 text-sm font-normal text-slate-700">
                          <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[#E3222A]" />
                          <span>{service}</span>
                        </li>
                      ))}
                    </ul>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        )}
      </div>
      {visibleTags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {visibleTags.map((tag) => (
            <span
              key={tag.id || tag.tag_short_name || tag.name}
              className={classNames(
                "inline-flex h-6 items-center rounded-md border px-2 text-[11px] font-medium",
                TAG_STYLES[tag.tag_color] || TAG_STYLES.slate,
              )}
            >
              {getTagLabel(tag)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Timing({ job }) {
  const completeLabel = job.status === "C" || job.status === "I" ? "Completed" : "Complete by";
  const completeValue = job.status === "C" || job.status === "I"
    ? job.completion_date
    : job.complete_before_formatted_date || job.completeBy;

  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.09em] text-slate-400 md:hidden">Timing</p>
      <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-2 text-[11px] leading-4 md:ml-auto md:mt-3 md:max-w-[260px] md:grid-cols-[80px_1fr] md:gap-x-3 md:text-sm md:leading-5">
        <dt className="whitespace-nowrap text-slate-400">Arrival</dt>
        <dd className="text-right font-semibold text-slate-700">{job.on_site ? "On site" : job.arrival_formatted_date || "TBD"}</dd>
        <dt className="whitespace-nowrap text-slate-400">Departure</dt>
        <dd className="text-right font-medium text-slate-600">{job.departure_formatted_date || "TBD"}</dd>
        <dt className="whitespace-nowrap text-slate-400">{completeLabel}</dt>
        <dd className="text-right font-semibold text-slate-900">{completeValue || "TBD"}</dd>
      </dl>
      {(job.isDueToday || job.isOverdue) && (
        <div className={classNames(
          "mt-3 flex items-center justify-end gap-1 text-[10px] font-bold md:gap-1.5 md:text-xs",
          job.isOverdue ? "text-red-600" : "text-amber-700",
        )}>
          <ExclamationCircleIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
          {job.isOverdue ? "Overdue" : "Due today"}
        </div>
      )}
    </div>
  );
}

function CustomerQueueJob({ job }) {
  return (
    <li data-testid="customer-queue-job" className="relative z-0 focus-within:z-40">
      <article className="group relative grid gap-4 rounded-xl border border-slate-200 bg-white px-5 py-5 shadow-sm hover:bg-slate-50/70 md:grid-cols-[minmax(250px,1.25fr)_minmax(190px,.95fr)_minmax(225px,1fr)_26px] md:items-center md:rounded-none md:border-0 md:px-6 md:shadow-none">
        <Link to={`/jobs/${job.id}/details`} aria-label={`Open ${job.tailNumber} job details`} className="absolute inset-0 z-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#E3222A] md:rounded-none" />

        <div className="pointer-events-none relative z-10 min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-base font-extrabold tracking-tight text-slate-950">{job.tailNumber}</span>
            {formatPurchaseOrder(job.purchase_order) && (
              <span className="text-xs font-medium text-slate-400">{formatPurchaseOrder(job.purchase_order)}</span>
            )}
            <div className="ml-auto flex items-center gap-2 md:hidden">
              <StatusBadge status={job.status} />
              <UnreadCommentsBadge count={job.comments_count} compact />
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 text-sm text-slate-600">
            <LocationMarkerIcon className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
            <span><strong className="text-slate-900">{job.airport?.initials || "—"}</strong> · {job.fbo?.name || "FBO not specified"}</span>
          </div>
          <div className="mt-1.5 flex items-start gap-2 text-sm text-slate-500">
            <AirplaneIcon className="mt-0.5 h-4 w-4 flex-none" />
            <span>{job.aircraftType?.name || "Aircraft not specified"}</span>
          </div>
        </div>

        <div className="pointer-events-none relative z-10 grid gap-0 rounded-lg bg-slate-50 p-3 md:contents md:bg-transparent md:p-0">
          <div className="order-2 min-w-0 border-t border-slate-200 pt-3 md:order-1 md:border-t-0 md:pt-0">
            <ServicesSummary job={job} />
          </div>
          <div className="order-1 min-w-0 pb-3 md:order-2 md:pb-0">
            <div className="hidden items-center justify-end gap-2 md:flex">
              <StatusBadge status={job.status} />
              <UnreadCommentsBadge count={job.comments_count} />
            </div>
            <Timing job={job} />
          </div>
        </div>

        <ChevronRightIcon
          data-testid="customer-job-chevron"
          className="pointer-events-none relative z-10 hidden h-5 w-5 text-slate-300 md:order-3 md:block"
        />
      </article>
    </li>
  );
}

function FilterRow({ checked, label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
      <span className={classNames(
        "flex h-5 w-5 flex-none items-center justify-center rounded border",
        checked ? "border-[#E3222A] bg-[#E3222A] text-white" : "border-slate-300 bg-white text-transparent",
      )}>
        <CheckIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

function FilterSection({ title, detail, children }) {
  return (
    <section className="border-b border-slate-200 px-5 py-5 last:border-b-0">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-slate-700">{title}</h3>
        {detail && <span className="text-xs font-medium text-slate-400">{detail}</span>}
      </div>
      {children}
    </section>
  );
}

function CustomerFilterDrawer({
  open,
  onClose,
  statuses,
  statusSelected,
  onSelectStatus,
  customers,
  customerSelected,
  onSelectCustomer,
  showCustomers,
  airports,
  airportSelected,
  onSelectAirport,
  tags,
  onToggleTag,
  onClearAll,
  resultCount,
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-slate-950/30 backdrop-blur-[1px]" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-6 sm:pl-10">
              <Transition.Child as={Fragment} enter="transform transition ease-in-out duration-200" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transform transition ease-in-out duration-200" leaveFrom="translate-x-0" leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-[380px] flex-col bg-white shadow-2xl">
                  <div className="border-b border-slate-200 px-5 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Dialog.Title className="flex items-center gap-2 text-lg font-extrabold text-slate-950">
                          <FilterIcon className="h-5 w-5 text-[#E3222A]" />Filters
                        </Dialog.Title>
                        <Dialog.Description className="mt-1 text-sm text-slate-500">Narrow the queue, then apply your selections.</Dialog.Description>
                      </div>
                      <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close filters">
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    <FilterSection title="Status">
                      {statuses.map((status) => (
                        <FilterRow key={status.id} checked={statusSelected.id === status.id} label={status.name} onClick={() => onSelectStatus(status)} />
                      ))}
                    </FilterSection>
                    {showCustomers && (
                      <FilterSection title="Customers" detail={`${customers.length} in queue`}>
                        {customers.map((customer) => (
                          <FilterRow key={customer.id} checked={customerSelected.id === customer.id} label={customer.name} onClick={() => onSelectCustomer(customer)} />
                        ))}
                      </FilterSection>
                    )}
                    <FilterSection title="Airports" detail={`${Math.max(airports.length - 1, 0)} in queue`}>
                      {airports.map((airport) => (
                        <FilterRow key={airport.id} checked={airportSelected.id === airport.id} label={airport.name} onClick={() => onSelectAirport(airport)} />
                      ))}
                    </FilterSection>
                    <FilterSection title="Tags">
                      {tags.map((tag) => (
                        <FilterRow key={tag.id} checked={Boolean(tag.selected)} label={tag.name} onClick={() => onToggleTag(tag)} />
                      ))}
                    </FilterSection>
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t border-slate-200 bg-white p-4">
                    <button type="button" onClick={onClearAll} className="h-10 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Clear all</button>
                    <button type="button" onClick={onClose} className="h-10 rounded-lg bg-[#E3222A] text-sm font-semibold text-white hover:bg-red-700">Show {resultCount} jobs</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default function CustomerJobsQueueRedesign({
  currentUser,
  jobs,
  loading,
  totalJobs,
  searchText,
  onSearchChange,
  onSearchKeyDown,
  onRefresh,
  filtersOpen,
  onFiltersOpen,
  onFiltersClose,
  statuses,
  statusSelected,
  onSelectStatus,
  customers,
  customerSelected,
  onSelectCustomer,
  airports,
  airportSelected,
  onSelectAirport,
  tags,
  onToggleTag,
  onClearAll,
}) {
  const customerName = customerSelected.id !== "All"
    ? customerSelected.name
    : currentUser.customerName;

  return (
    <div className="min-h-screen bg-[#F4F6F8] pb-32 text-slate-900">
      <div className="mx-auto w-full max-w-[1480px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-950 lg:text-[28px]">Open Jobs</h1>
              <span className="inline-flex h-6 items-center rounded-full bg-slate-900 px-2.5 text-xs font-semibold text-white">{totalJobs} open</span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Current service requests{customerName ? ` for ${customerName}` : ""}
            </p>
          </div>
          {currentUser.isCustomer && (
            <Link to="/create-job" className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#E3222A] px-4 text-sm font-semibold text-white shadow-sm hover:bg-red-700">
              <PlusIcon className="h-5 w-5" />New job
            </Link>
          )}
        </header>

        <section className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center" aria-label="Customer queue controls">
          <label className="relative min-w-0 flex-1">
            <span className="sr-only">Search jobs</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchText}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Search by tail number or P.O"
              className="h-10 w-full rounded-lg border-0 bg-slate-50 pl-10 pr-3 text-sm text-slate-800 shadow-none placeholder:text-slate-400 focus:ring-2 focus:ring-sky-500"
            />
          </label>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <button type="button" onClick={onFiltersOpen} className="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <FilterIcon className="h-5 w-5" />Filters
            </button>
            <button type="button" onClick={onRefresh} disabled={loading} className="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <RefreshIcon className={classNames("h-5 w-5", loading && "animate-spin")} />Update queue
            </button>
          </div>
        </section>

        {loading ? (
          <Loader />
        ) : jobs.length ? (
          <section className="space-y-3 md:space-y-0 md:rounded-xl md:border md:border-slate-200 md:bg-white md:shadow-sm" aria-label="Customer jobs queue results">
            <div className="hidden grid-cols-[minmax(250px,1.25fr)_minmax(190px,.95fr)_minmax(225px,1fr)_26px] rounded-t-xl border-b border-slate-200 bg-slate-50 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 md:grid">
              <span>Job &amp; location</span>
              <span>Services</span>
              <span className="text-right">Status &amp; timing</span>
              <span aria-hidden="true" />
            </div>
            <ul data-testid="customer-queue-list" className="md:divide-y md:divide-slate-200">
              {jobs.map((job) => <CustomerQueueJob key={job.id} job={job} />)}
            </ul>
          </section>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
            <h2 className="text-sm font-bold text-slate-900">No jobs found</h2>
            <p className="mt-1 text-sm text-slate-500">Try adjusting the queue filters or create a new job.</p>
            {currentUser.isCustomer && (
              <Link to="/create-job" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#E3222A] px-4 py-2 text-sm font-semibold text-white">
                <PlusIcon className="h-5 w-5" />New job
              </Link>
            )}
          </div>
        )}
      </div>

      <CustomerFilterDrawer
        open={filtersOpen}
        onClose={onFiltersClose}
        statuses={statuses}
        statusSelected={statusSelected}
        onSelectStatus={onSelectStatus}
        customers={customers}
        customerSelected={customerSelected}
        onSelectCustomer={onSelectCustomer}
        showCustomers={currentUser.hasExtraCustomers}
        airports={airports}
        airportSelected={airportSelected}
        onSelectAirport={onSelectAirport}
        tags={tags}
        onToggleTag={onToggleTag}
        onClearAll={onClearAll}
        resultCount={totalJobs}
      />
    </div>
  );
}
